/**
 * EZOPEN Error Suppression Setup
 * This file MUST be imported before React in index.tsx
 * It patches all error reporting mechanisms to suppress EZOPEN SDK errors
 */

// Helper to detect EZOPEN errors
function isEZOPENError(msg: string, src?: string, stack?: string): boolean {
  msg = String(msg || '').toLowerCase();
  src = String(src || '').toLowerCase();
  stack = String(stack || '').toLowerCase();
  
  return msg.includes('wasmplayer') || 
         msg.includes('jsplugin') || 
         msg.includes('ezopen') ||
         msg.includes("reading 'width'") ||
         msg.includes('cannot read properties of null') ||
         msg.includes('unknown promise rejection') ||
         src.includes('wasmplayer') ||
         src.includes('jsplugin') ||
         src.includes('ezopen') ||
         stack.includes('wasmplayer') ||
         stack.includes('jsplugin') ||
         stack.includes('ezopen');
}

// 1. Patch window.reportError if it exists (modern browsers)
if (typeof window.reportError === 'function') {
  const originalReportError = window.reportError;
  window.reportError = function(error: any) {
    const msg = error?.message || String(error || '');
    const stack = error?.stack || '';
    if (isEZOPENError(msg, '', stack)) {
      return; // Suppress
    }
    return originalReportError.call(window, error);
  };
}

// 2. Patch ErrorEvent constructor to filter EZOPEN errors
const originalErrorEvent = window.ErrorEvent;
(window as any).ErrorEvent = function(type: string, eventInitDict?: ErrorEventInit) {
  if (eventInitDict) {
    const msg = eventInitDict.message || eventInitDict.error?.message || '';
    const src = eventInitDict.filename || '';
    const stack = eventInitDict.error?.stack || '';
    if (isEZOPENError(msg, src, stack)) {
      // Return a dummy event that won't trigger the overlay
      return new originalErrorEvent(type, {
        ...eventInitDict,
        message: '',
        error: undefined,
        filename: '',
        lineno: 0,
        colno: 0
      });
    }
  }
  return new originalErrorEvent(type, eventInitDict);
};

// 3. Intercept console.error early (before React Error Overlay attaches)
const originalConsoleError = console.error;
console.error = function(...args: any[]) {
  const errorMsg = args.map(arg => String(arg || '')).join(' ');
  if (isEZOPENError(errorMsg, '', '')) {
    return; // Suppress completely
  }
  originalConsoleError.apply(console, args);
};

// 4. Intercept console.warn for good measure
const originalConsoleWarn = console.warn;
console.warn = function(...args: any[]) {
  const warnMsg = args.map(arg => String(arg || '')).join(' ');
  if (isEZOPENError(warnMsg, '', '')) {
    return; // Suppress
  }
  originalConsoleWarn.apply(console, args);
};

// 5. Add aggressive event listeners at the EARLIEST capture phase
const suppressEZOPENError = (event: ErrorEvent) => {
  const msg = event.message || event.error?.message || '';
  const src = event.filename || '';
  const stack = event.error?.stack || '';
  
  if (isEZOPENError(msg, src, stack)) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    // Note: ErrorEvent.error is read-only, but stopping propagation prevents React from seeing it
  }
};

const suppressEZOPENRejection = (event: PromiseRejectionEvent) => {
  const reason = event.reason;
  
  if (reason === undefined || reason === null) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return;
  }
  
  const msg = typeof reason === 'string' ? reason : 
              reason instanceof Error ? reason.message : 
              String(reason);
  const stack = reason instanceof Error ? (reason.stack || '') : '';
  
  if (isEZOPENError(msg, '', stack)) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    // Note: Cannot modify event.reason as it's read-only
  }
};

// Add listeners at CAPTURE phase (highest priority)
window.addEventListener('error', suppressEZOPENError, true);
window.addEventListener('unhandledrejection', suppressEZOPENRejection, true);

// Also add at BUBBLE phase as backup
window.addEventListener('error', suppressEZOPENError, false);
window.addEventListener('unhandledrejection', suppressEZOPENRejection, false);

// 6. Override window.onerror
window.onerror = function(message: any, source?: string, lineno?: number, colno?: number, error?: Error) {
  const msg = String(message || '');
  const src = String(source || '');
  const stack = error?.stack || '';
  
  if (isEZOPENError(msg, src, stack)) {
    return true; // Suppress and prevent default handling
  }
  
  return false; // Let other errors through
};

// 7. Override window.onunhandledrejection
window.onunhandledrejection = function(event: PromiseRejectionEvent) {
  const reason = event.reason;
  
  if (reason === undefined || reason === null) {
    event.preventDefault();
    return;
  }
  
  const msg = typeof reason === 'string' ? reason : 
              reason instanceof Error ? reason.message : 
              String(reason);
  const stack = reason instanceof Error ? (reason.stack || '') : '';
  
  if (isEZOPENError(msg, '', stack)) {
    event.preventDefault();
  }
};

export {};
